import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Eye,
  Info,
  MoreVertical,
  Pencil,
  FileEdit,
} from "lucide-react";
import clsx from "clsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import { getStatusBadge, getTableValue } from "@/utils/helpers";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Badge } from "./ui/badge";
import { useLocation, useNavigate } from "react-router";
import {
  SelectScopeHeaderControl,
  type SelectScope,
} from "./SelectScopeHeaderControl";

// TYPES
export interface ColumnHeader {
  id: string;
  label: string;
  isSortable: boolean;
}

export type RowData = Record<string, any> & {
  id?: string | number;
};

export type SortType = 0 | 1 | -1;

export interface DataTableProps {
  columnHeaders: any[];
  columnsData: any[];
  isActionColumn?: boolean;
  actionFn?: (row: any) => void;
  editActionFn?: (row: any) => void;
  sortFn?: (headerId: string, sortType: string, sortKey?: string | null | undefined ) => void;
  loading?: boolean;
  limit: number;
  viewColumnData: any[];
  isSelectable: boolean;
  selected: any;
  setSelected: (data: any) => void;
  /** When true, every row is treated as selected except `unselectedPatientIds`. */
  selectAll?: boolean;
  unselectedPatientIds?: any[];
  setSelectAll?: (value: boolean) => void;
  setUnselectedPatientIds?: (ids: any[]) => void;
  /** Gmail-style All / Visible / None menu in the header (default true when selectable). */
  enableSelectScopeMenu?: boolean;
  clickRow: (data: any) => void;
  moreActions?: any[];
  clickCell?: any;
  isActionDisabled?: (row: any) => boolean;
  /** When true, action menu (⋮) is not shown for that row. */
  isActionsHidden?: (row: any) => boolean;
  // external pagination props
  sort?: { sortKey: string, sortType: string, sortKeyMain?: string };
  className?: string;
  columnOrder?: string[];
  onColumnOrderChange?: (nextOrder: string[]) => void;
  changeRowColor?: (row: RowData) => string | undefined;
}

interface SortConfig {
  [key: string]: SortType;
}

const DataTable: React.FC<DataTableProps> = ({
  columnHeaders = [],
  columnsData = [],
  viewColumnData,
  isActionColumn = false,
  actionFn = () => {},
  editActionFn,
  sortFn = () => {},
  loading = false,
  limit = 10,
  isSelectable = false,
  selected,
  setSelected,
  selectAll = false,
  unselectedPatientIds = [],
  setSelectAll,
  setUnselectedPatientIds,
  enableSelectScopeMenu = true,
  className = "",
  clickRow,
  sort,
  moreActions = [],
  clickCell,
  isActionDisabled,
  isActionsHidden,
  columnOrder: externalColumnOrder,
  onColumnOrderChange,
  changeRowColor,
}) => {
  const [sortConfig, setSortConfig] = useState<SortConfig>({});
  const navigate = useNavigate();
  const {pathname} = useLocation()

  // Column ordering state for draggable columns
  const [columnOrder, setColumnOrder] = useState<string[]>(() =>
    (columnHeaders || []).map((h: any) => h.id),
  );
  const [dragColId, setDragColId] = useState<string | null>(null);

  // Keep columnOrder in sync with incoming columnHeaders (add new, drop removed)
  useEffect(() => {
    const ids = (columnHeaders || []).map((h: any) => h.id);
    if (Array.isArray(externalColumnOrder) && externalColumnOrder.length > 0) {
      const next = [
        ...externalColumnOrder.filter((id) => ids.includes(id)),
        ...ids.filter((id) => !externalColumnOrder.includes(id)),
      ];
      setColumnOrder(next);
      return;
    }
    setColumnOrder((prev) => {
      if (!prev || prev.length === 0) return ids;
      const next = [
        // keep existing ids that still exist
        ...prev.filter((id) => ids.includes(id)),
        // append any new ids
        ...ids.filter((id) => !prev.includes(id)),
      ];
      return next;
    });
  }, [columnHeaders, externalColumnOrder]);

  const orderedHeaders = React.useMemo(() => {
    const idOrder = columnOrder && columnOrder.length
      ? columnOrder
      : (columnHeaders || []).map((h: any) => h.id);
    const byId: Record<string, any> = {};
    (columnHeaders || []).forEach((h: any) => {
      byId[h.id] = h;
    });
    return idOrder.map((id) => byId[id]).filter(Boolean);
  }, [columnHeaders, columnOrder]);

  // Check if a column is an ID column
  const isIdColumn = (headerId: string): boolean => {
    const idPatterns = [
      "patientId",
      "Patient ID",
      "providerId",
      "practitionerId",
      "caretakerId",
      "organizationId",
      "facilityId",
    ];
    const header = columnHeaders.find(h => h.id === headerId);
    
    // Check if it's a known ID pattern
    if (idPatterns.includes(headerId)) {
      return true;
    }
    
    // Check if label is "ID"
    if (header?.label === "ID") {
      return true;
    }
    
    return false;
  };

  // Determine the route for an ID column
  const getRouteForIdColumn = (headerId: string, rowId: string | number | undefined) => {
    if (!rowId) return null;
    
    const idColumnRoutes: Record<string, string> = {
      patientId: "/patients",
      "Patient ID": "/patients",
      providerId: "/providers",
      practitionerId: "/practitioners",
      caretakerId: "/caretakers",
      organizationId: "/organizations",
      facilityId: "/organizations",
    };

    // Direct match
    if (idColumnRoutes[headerId]) {
      return `${idColumnRoutes[headerId]}/${rowId}`;
    }

    // Check if label is "ID" - try to infer from headerId
    const header = columnHeaders.find(h => h.id === headerId);
    if (header?.label === "ID" || header?.label === "Patient ID") {
      // Try to match patterns in headerId
      if (headerId.toLowerCase().includes("patient")) {
        return `/patients/${rowId}`;
      } else if (headerId.toLowerCase().includes("provider")) {
        return `/providers/${rowId}`;
      } else if (headerId.toLowerCase().includes("practitioner")) {
        return `/practitioners/${rowId}`;
      } else if (headerId.toLowerCase().includes("caretaker")) {
        return `/caretakers/${rowId}`;
      } else if (headerId.toLowerCase().includes("organization")) {
        return `/organizations/${rowId}`;
      } else if (headerId.toLowerCase().includes("facility")) {
        return `/organizations/${rowId}`;
      }
    }

    return null;
  };

  // Slice Data for current page
  const paginatedData = viewColumnData ? viewColumnData : columnsData;
  console.log({viewColumnData, columnsData})

  // Handle sorting
  const handleSort = (headerId: string) => {
    // console.log({headerId})
    const header = columnHeaders.find((h) => h.id === headerId);
    // console.log("xx", header)
    if (!header?.isSortable) return;

    let newSortType: SortType = 0;

    if (sortConfig[headerId] === undefined || sortConfig[headerId] === 0) {
      newSortType = 1; // asc
    } else if (sortConfig[headerId] === 1) {
      newSortType = -1; // desc
    } else {
      newSortType = 0; // reset
    }

    // console.log({newSortType})

    setSortConfig({ [headerId]: newSortType });
    sortFn(headerId, newSortType == 0 ? "" : newSortType == 1 ? "asc" : "desc", header?.sortKey);
  };

  // Sorting Icon
  const getSortIcon = (headerId: string) => {
    // console.log('running');
    const sortType = sortConfig[headerId];
    // console.log({sortType})
    if (sortType === 1) return <ArrowUp className="ml-2 h-4 w-4" />;
    if (sortType === -1) return <ArrowDown className="ml-2 h-4 w-4" />;
    return <ArrowUpDown className="ml-2 h-4 w-4" />;
  };

  // Checkbox handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectAll?.(false);
      setUnselectedPatientIds?.([]);
      setSelected(columnsData.map((row) => row._id ?? row));
    } else {
      setSelectAll?.(false);
      setUnselectedPatientIds?.([]);
      setSelected([]);
    }
  };

  const handleSelectScope = (scope: SelectScope) => {
    if (scope === "all") {
      setSelectAll?.(true);
      setUnselectedPatientIds?.([]);
      setSelected([]);
      return;
    }
    if (scope === "visible") {
      setSelectAll?.(false);
      setUnselectedPatientIds?.([]);
      setSelected(columnsData.map((row) => row._id ?? row));
      return;
    }
    setSelectAll?.(false);
    setUnselectedPatientIds?.([]);
    setSelected([]);
  };

  const handleSelectRow = (row: any, checked: boolean) => {
    const rowId = row._id ?? row;
    if (selectAll) {
      const current = Array.isArray(unselectedPatientIds)
        ? [...unselectedPatientIds]
        : [];
      if (checked) {
        setUnselectedPatientIds?.(current.filter((id: any) => id !== rowId));
      } else if (!current.includes(rowId)) {
        setUnselectedPatientIds?.([...current, rowId]);
      }
      return;
    }
    if (checked) {
      setSelected([...selected, rowId]);
    } else {
      setSelected(selected.filter((id: any) => id !== rowId));
    }
  };

  const isRowSelected = (row: any) => {
    const rowId = row._id ?? row;
    if (selectAll) {
      return !unselectedPatientIds?.includes(rowId);
    }
    return selected.includes(rowId);
  };

  const isAllVisibleSelected =
    paginatedData.length > 0 &&
    paginatedData.every((row) => isRowSelected(row));

  const headerChecked: boolean | "indeterminate" = (() => {
    if (selectAll) {
      return unselectedPatientIds?.length > 0 ? "indeterminate" : true;
    }
    if (!selected?.length) return false;
    if (isAllVisibleSelected) return true;
    return "indeterminate";
  })();

  const selectionScope: SelectScope = selectAll
    ? "all"
    : selected?.length && isAllVisibleSelected
      ? "visible"
      : "none";

  useEffect(() => {
    // console.log({sort})
    if(sort?.sortKey){
      setSortConfig({ [sort.sortKey]: sort.sortType == "asc" ? 1 : sort.sortType == "desc" ? -1 : 0 });
    }
  }, [JSON.stringify(sort?.sortKey)])

  return (
    <div className="w-full space-y-4">
      <div className="w-full min-w-0">
        <Table className={className}>
          {/* HEADER */}
          <TableHeader className=" ">
            <TableRow className="border-grey">
              {isSelectable && (
                <TableHead className="w-14 p-0">
                  {enableSelectScopeMenu  ? (
                    <SelectScopeHeaderControl
                      disabled={loading || paginatedData.length === 0}
                      checked={headerChecked}
                      scope={selectionScope}
                      onScopeSelect={handleSelectScope}
                    />
                  ) : (
                    <Checkbox
                      checked={
                        headerChecked === "indeterminate"
                          ? "indeterminate"
                          : headerChecked
                      }
                      onCheckedChange={handleSelectAll}
                      disabled={loading || paginatedData.length === 0}
                    />
                  )}
                </TableHead>
              )}
              {orderedHeaders.map((header) => (
                <TableHead
                  key={header.id}
                  className="font-semibold teachers cursor-pointer p-0"
                  draggable
                  onDragStart={() => setDragColId(header.id)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (!dragColId || dragColId === header.id) return;
                    setColumnOrder((prev) => {
                      const arr = [...prev];
                      const from = arr.indexOf(dragColId);
                      const to = arr.indexOf(header.id);
                      if (from === -1 || to === -1) return prev;
                      arr.splice(from, 1);
                      arr.splice(to, 0, dragColId);
                      onColumnOrderChange?.(arr);
                      return arr;
                    });
                  }}
                >
                  {header.isSortable ? (
                    <div
                      onClick={() =>
                        !loading &&
                        paginatedData.length > 0 &&
                        handleSort(header.id)
                      }
                      className={clsx(
                        "h-8 px-2 w-full flex justify-start gap-2 items-center",
                        loading || paginatedData.length == 0
                          ? "cursor-not-allowed"
                          : "cursor-pointer",
                      )}
                    >
                      {header.label}
                      {getSortIcon(header.id)}
                    </div>
                  ) : (
                    <span className="px-2 text-center">{header.label}</span>
                  )}
                </TableHead>
              ))}

              {(isActionColumn || moreActions.length>0) && (
                <TableHead className="font-semibold sticky right-0 bg-white">
                  Actions
                </TableHead>
              )}
            </TableRow>
          </TableHeader>

          {/* BODY */}
          <TableBody>
            {loading ? (
              <>
                {[...Array(limit)].map((row, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {isSelectable && (
                      <TableCell className="w-12">
                        <Checkbox disabled />
                      </TableCell>
                    )}
                    {orderedHeaders.map((header, idx) => (
                      <TableCell key={header.id} className="">
                        <div className="w-[80%] h-4 bg-gray-300 rounded-full animate-pulse"></div>
                      </TableCell>
                    ))}
                    {(isActionColumn || moreActions.length>0 ) && (
                      <TableCell className="sticky right-0 bg-white">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled
                              className="gap-2"
                            >
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            {/* <p>View Details</p> */}
                          </TooltipContent>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </>
            ) : (
              <>
                {paginatedData.length > 0 ? (
                  paginatedData.map((row, rowIndex) => (
                    <TableRow className={clsx( isRowSelected(row) ?"bg-primary/10" : changeRowColor ? changeRowColor?.(row) : "")} key={row.id ?? rowIndex}>
                      {isSelectable && (
                        <TableCell className="w-12">
                          <Checkbox
                            checked={isRowSelected(row)}
                            onCheckedChange={(checked) =>
                              handleSelectRow(row, checked as boolean)
                            }
                          />
                        </TableCell>
                      )}
                      {orderedHeaders.map((header) => {
                        const clickObj = clickCell.find((c:any)=> c.headerKey == header.id)
                          const isClick = !!clickObj ? clickObj.isClickable(row) : false
                      isClick &&     console.log({clickObj, isClick})
                        if(header.id == "status" || header.id == "typeOfPatient" || header.id == 'lastCarePlanStatus' || header.id == 'lastTreatmentPlanStatus' || header.id == "eligibleForRpm"){
                          const obj = getStatusBadge(row[header.id]);
                          // Convert to string before calling split to avoid errors
                          const statusValue = String(row[header.id] || "");
                          const isInvalidWithNotes = header.id === "status" && String(row[header.id] || "").toLowerCase() === "invalid" && row.resolutionNotes;

                          return (
                            <TableCell
                          className="text-[#6E6E6E] text-sm "
                          onClick={() =>
                            clickRow && clickRow(columnsData?.[rowIndex])
                          }
                          key={header.id}
                        >
                          <div className="flex justify-start items-center gap-1.5">
                            <Badge variant={'outline'} className="capitalize py-1 px-5 rounded-full teachers"  style={{backgroundColor : obj.bgColor, color: obj.mainColor, borderColor : obj.mainColor}} >{statusValue?.split("_").join(" ")} </Badge>
                            {isInvalidWithNotes && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="inline-flex cursor-help">
                                    <Info className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-xs">
                                  <p className="font-medium">Resolution notes</p>
                                  <p className="text-xs mt-0.5">{row.resolutionNotes}</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        </TableCell>
                          )
                        }

                       // Check if this is an ID column
                       const isId = isIdColumn(header.id);
                       // For navigation: default row _id; Critical Report etc. use `Patient Mongo ID` for profile link
                       const resolveNavIdForCell = (): string | number | undefined => {
                         if (header.id === "Patient ID") {
                           const raw =
                             row["Patient Mongo ID"] ??
                             row["patientMongoId"] ??
                             row.patientMongoId;
                           if (raw != null && typeof raw === "object") {
                             const o = raw as { _id?: string };
                             return o._id;
                           }
                           if (raw != null && String(raw).trim() !== "")
                             return typeof raw === "string" || typeof raw === "number"
                               ? raw
                               : String(raw);
                         }
                         return row._id || row.id;
                       };
                       const actualId = resolveNavIdForCell();
                       const displayedId = row[header.id]; // This is what we show in the table
                       const route = isId && actualId ? getRouteForIdColumn(header.id, actualId) : null;
                       
                       // Find "View" action from moreActions to use the same handler
                       // For patientId, always navigate to patient profile, not to View action
                       const viewAction =
                         header.id === "patientId" || header.id === "Patient ID"
                           ? null
                           : moreActions.find((action: any) => 
                         action.label?.toLowerCase().includes("view")
                       );

                       return ( <TableCell
                          className={clsx(
                            "text-sm text-tertiary font-[500] teachers ",
                            header.id === "content" && "max-w-[280px] min-w-0 break-words whitespace-normal align-top",
                           ( isId && (route || viewAction) ||isClick) && "cursor-pointer hover:text-primary"
                          )}
                          onClick={(e) => {
                            // console.log("CLICKED")
                            if (isId) {
                              e.stopPropagation();
                              // For patientId, always navigate to patient profile using _id
                              // For other IDs, use View action if available, otherwise navigate
                              if (
                                (header.id === "patientId" || header.id === "Patient ID") &&
                                route
                              ) {
                                navigate(route, {state : {from :pathname}});
                              } else if (viewAction && viewAction.onClick) {
                                viewAction.onClick(columnsData?.[rowIndex] || row);
                              } else if (route) {
                                navigate(route ,{state : {from :pathname}});
                              }

                            } 
                            
                             else if (isClick){
                              // console.log("CLICKED", row)
                                 clickObj?.onClick(row)
                            }
                            else if (clickRow) {
                              clickRow(columnsData?.[rowIndex]);
                            }
                           
                          }}
                          key={header.id}
                        >
                          {row[header.id] !== undefined ? (
                           ( isId && (route || viewAction) ||isClick)? (
                              <span className="text-primary hover:underline font-medium">
                                {React.isValidElement(displayedId) ? displayedId : getTableValue(displayedId)}
                              </span>
                            ) : (
                              React.isValidElement(displayedId) ? displayedId : getTableValue(displayedId)
                            )
                          ) : (
                            "-"
                          )}
                        </TableCell>)
})}

                      {(isActionColumn || moreActions.length > 0) && (
                        <TableCell className="sticky right-0 bg-white">
                          {!isActionsHidden?.(columnsData?.[rowIndex]) &&
                          (isActionColumn ||
                            moreActions.some(
                              (action: any) =>
                                action.isShow &&
                                action.isShow(columnsData?.[rowIndex] || row),
                            )) ? (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                >
                                  <MoreVertical className="h-4 w-4" />
                                  <span className="sr-only">Open menu</span>
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-48">
                               {isActionColumn && (
                                  <DropdownMenuItem
                                    onClick={() => {
                                      const row = columnsData?.[rowIndex];
                                      if (!isActionDisabled?.(row)) {
                                        actionFn(row);
                                      }
                                    }}
                                    disabled={isActionDisabled?.(columnsData?.[rowIndex])}
                                  >
                                    <Pencil className="mr-2 h-4 w-4" />
                                    <span>Edit</span>
                                  </DropdownMenuItem>
                                )}
                                {/* {editActionFn && (
                                  <DropdownMenuItem
                                    onClick={() =>
                                      editActionFn(columnsData?.[rowIndex])
                                  }
                                >
                                  <FileEdit className="mr-2 h-4 w-4" />
                                  <span>Edit</span>
                                </DropdownMenuItem>
                              )} */}
                                {moreActions
                                  .filter(
                                    (action) =>
                                      action.isShow &&
                                      action.isShow(columnsData?.[rowIndex]),
                                  )
                                  .map((action, index) => {
                                    const row = columnsData?.[rowIndex];
                                    const isDisabled = action.isDisabled
                                      ? action.isDisabled(row)
                                      : false;
                                    if (action.render) {
                                      return (
                                        <DropdownMenuItem
                                          key={index}
                                          className="focus:bg-accent cursor-default"
                                          disabled={isDisabled}
                                          onSelect={(e) => {
                                            if (action.preventClose) e.preventDefault();
                                          }}
                                        >
                                          {action.render(row)}
                                        </DropdownMenuItem>
                                      );
                                    }
                                    return (
                                      <DropdownMenuItem
                                        key={index}
                                        onClick={() => {
                                          if (!isDisabled && action.onClick) {
                                            action.onClick(row);
                                          }
                                        }}
                                        disabled={isDisabled}
                                      >
                                        <span className="mr-2">{action.icon}</span>
                                        <span>{action.label}</span>
                                      </DropdownMenuItem>
                                    );
                                  })}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          ) : null}
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow className="relative h-24 min-w-full">
                    <TableCell
                      colSpan={
                        columnHeaders.length +
                        (isActionColumn ? 1 : 0) +
                        (isSelectable ? 1 : 0)
                      }
                      className="text-center text-muted-foreground w-full relative"
                    >
                      <div className="w-fit sticky left-1/2 -translate-x-1/2">
                        No data found
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default DataTable;
